/**
 * RunAllConfirmDialog — M3 Run-all entry confirm (SDialog default pattern:
 * left close X, ONE primary "Run all"). Review toggles default ON (D1):
 *   - "Review drafts before apply" → firstSprintApplyMode 'draft_review'
 *     + topic-draft review pause;
 *   - "Confirm pillars manually" → NO autoConfirmPillars; explicit
 *     confirm checkpoint in the staged panel.
 */

import React, { useEffect, useState } from 'react';
import {
  Checkbox, FormControlLabel, Stack, Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { RunAllOptions } from './useRunAllOrchestrator';

export interface RunAllConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: RunAllOptions) => void;
}

export const RunAllConfirmDialog: React.FC<RunAllConfirmDialogProps> = ({
  open, onClose, onConfirm,
}) => {
  const [reviewDrafts, setReviewDrafts] = useState(true);
  const [confirmPillarsManually, setConfirmPillarsManually] = useState(true);

  useEffect(() => {
    if (!open) return;
    setReviewDrafts(true);
    setConfirmPillarsManually(true);
  }, [open]);

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Run the whole content-plan flow"
      maxWidth="sm"
      fullWidth
      data-testid={CONTENT_PLAN.RUNALL_DIALOG}
      showCloseButton={false}
      primaryAction={{
        label: 'Run all',
        onClick: () => onConfirm({ reviewDrafts, confirmPillarsManually }),
        'data-testid': CONTENT_PLAN.RUNALL_CONFIRM,
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">
          Chains the pipeline stages in order — author profile, pillars, topic
          backlog, strategy, first-sprint posts. Stages whose output already
          exists are skipped; review checkpoints pause the chain until you act.
          The staged progress list stays on this page.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={reviewDrafts}
              onChange={(event) => setReviewDrafts(event.target.checked)}
              size="small"
              data-testid={CONTENT_PLAN.RUNALL_TOGGLE_REVIEW_DRAFTS}
            />
          }
          label={
            <Typography variant="body2">
              Review drafts before apply
              <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                (topics and posts pause for your review)
              </Typography>
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmPillarsManually}
              onChange={(event) => setConfirmPillarsManually(event.target.checked)}
              size="small"
              data-testid={CONTENT_PLAN.RUNALL_TOGGLE_CONFIRM_PILLARS}
            />
          }
          label={
            <Typography variant="body2">
              Confirm pillars manually
              <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                (no auto-confirm — the chain pauses at the pillar checkpoint)
              </Typography>
            </Typography>
          }
        />
      </Stack>
    </SDialog>
  );
};

export default RunAllConfirmDialog;
